/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * The join between the Breakdown's grouping tree (run grain) and the review
 * findings (finding grain) — PLAT-1819 direction C: one decision table.
 *
 * The two grains share only SOME dimensions. A finding carries org, repo,
 * pr and (via the same catalog projection the rows get) team — so quality
 * cells are exact under those groupings. It carries no model, author,
 * trigger, job or savings method: under any of those the cells are WITHHELD
 * with the reason attached, never approximated (the panel's governing rule).
 *
 * Period scoping is exact and mirrors the server's own definitions: a finding
 * belongs to the displayed period when its last review activity (updatedAt)
 * falls inside it — ISO week computed UTC like the server's isoWeekOf, days
 * and months in the viewer's selected timezone like every other figure.
 */
import {
  Dimension,
  GroupNode,
  ReviewCell,
  ReviewWithheld,
} from './claudeMetricsAnalysis';
import { ReviewFindingRow } from './claudeReviewQuality';
import { todayIn } from './claudeMetricsView';

/** A finding enriched with the SAME team projection the token rows get. */
export type TeamedFinding = ReviewFindingRow & { team: string };

/** Dimensions a finding can be attributed to exactly. */
export const REVIEW_JOINABLE: ReadonlySet<Dimension> = new Set([
  'org',
  'repo',
  'team',
  'pr',
] as Dimension[]);

/**
 * Mirrors the server's isoWeekOf (claudeMetricsRollup.ts) byte for byte in
 * semantics: UTC-based, Monday-start ISO weeks. The weekly view's buckets are
 * that function's output, so membership here uses the same calendar.
 */
export function isoWeekOfUtc(d: Date): { isoYear: number; isoWeek: number } {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const isoYear = t.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const isoWeek = Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return { isoYear, isoWeek };
}

export type PeriodScope =
  | { granularity: 'week'; isoYear: number; isoWeek: number }
  | { granularity: 'day'; day: string; tz: string }
  | { granularity: 'month'; month: string; tz: string };

/**
 * Membership test: did this finding's last review activity land in the
 * displayed period? Invalid timestamps are excluded (never guessed in).
 */
export function makePeriodFilter(scope: PeriodScope): (f: ReviewFindingRow) => boolean {
  return f => {
    const at = new Date(f.updatedAt);
    if (Number.isNaN(at.getTime())) return false;
    if (scope.granularity === 'week') {
      const w = isoWeekOfUtc(at);
      return w.isoYear === scope.isoYear && w.isoWeek === scope.isoWeek;
    }
    const day = todayIn(scope.tz, at);
    if (scope.granularity === 'day') return day === scope.day;
    return day.slice(0, 7) === scope.month;
  };
}

/**
 * Whether the findings window (the /review-quality trailing fetch) fully
 * covers the displayed period. When it does not, quality cells are withheld:
 * a period the window only partially reaches would show a truthless partial
 * count as if it were the period's total.
 *
 * The bound is deliberately conservative for day/month (a 14h slack covers
 * every real UTC offset): uncertainty withholds, it never widens.
 */
export function windowCoversPeriod(scope: PeriodScope, sinceIso: string): boolean {
  const since = new Date(sinceIso).getTime();
  if (Number.isNaN(since)) return false;
  let periodStart: number;
  if (scope.granularity === 'week') {
    // UTC midnight of the ISO week's Monday (mirrors the server's isoWeekStart).
    const jan4 = new Date(Date.UTC(scope.isoYear, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const week1Monday = jan4.getTime() - (jan4Day - 1) * 86_400_000;
    periodStart = week1Monday + (scope.isoWeek - 1) * 7 * 86_400_000;
  } else {
    const dayStr = scope.granularity === 'day' ? scope.day : `${scope.month}-01`;
    periodStart = new Date(`${dayStr}T00:00:00Z`).getTime() - 14 * 3_600_000;
  }
  return Number.isFinite(periodStart) && periodStart >= since;
}

/** One narrowing step: a group path segment or a focus chip. */
export type ReviewConstraint = { dim: Dimension; value: string };

/**
 * Applies one joinable constraint. Callers must have routed unjoinable
 * dimensions to a withheld reason first — this throws on them so a future
 * dimension cannot silently join on nothing and return everything.
 */
export function matchesConstraint(f: TeamedFinding, c: ReviewConstraint): boolean {
  switch (c.dim) {
    case 'org':
      return f.org === c.value;
    case 'repo':
      return f.repo === c.value;
    case 'team':
      return f.team === c.value;
    case 'pr':
      // The tree's pr values are qualified org/repo#n (see the PR link in the
      // panel); findings carry the parts.
      return `${f.org}/${f.repo}#${f.pr}` === c.value;
    default:
      throw new Error(`review join: dimension '${c.dim}' is not joinable`);
  }
}

const cellOf = (rows: readonly TeamedFinding[]): ReviewCell => {
  const cell: ReviewCell = {
    findings: 0,
    fixed: 0,
    open: 0,
    closed: 0,
    declined: 0,
    advisory: 0,
  };
  for (const f of rows) {
    if (f.advisory) {
      cell.advisory++;
      continue;
    }
    cell.findings++;
    cell[f.state]++;
  }
  if (cell.findings > 0) cell.fixRate = cell.fixed / cell.findings;
  return cell;
};

/**
 * Attaches review cells to a built grouping tree, in place.
 *
 * `findings` must already be scoped by the panel to the displayed period AND
 * the joinable focus chips — the same pre-filtering contract runCounts and
 * savings follow. `withholdAll` carries a tree-wide reason (truncated payload,
 * window not covering the period, an unjoinable focus chip) and wins over any
 * per-node join.
 */
export function attachReviewQuality(
  nodes: GroupNode[],
  findings: readonly TeamedFinding[],
  withholdAll?: ReviewWithheld,
): void {
  for (const node of nodes) {
    if (withholdAll !== undefined) {
      node.reviewWithheldBy = withholdAll;
      attachReviewQuality(node.children, [], withholdAll);
      continue;
    }
    if (!REVIEW_JOINABLE.has(node.dim)) {
      // The reason is the node's own dimension; descendants inherit it — a
      // repo nested under a model is still a slice of model-split rows.
      node.reviewWithheldBy = node.dim;
      attachReviewQuality(node.children, [], node.dim);
      continue;
    }
    const scoped = findings.filter(f =>
      matchesConstraint(f, { dim: node.dim, value: node.value }),
    );
    node.review = cellOf(scoped);
    attachReviewQuality(node.children, scoped);
  }
}

/**
 * The finding rows behind one group row, for the inline drill-down. Same
 * constraint chain the cell was computed from, so the rows always sum to the
 * numbers shown — newest review activity first.
 */
export function findingsForPath(
  findings: readonly TeamedFinding[],
  constraints: readonly ReviewConstraint[],
): TeamedFinding[] {
  let scoped = [...findings];
  for (const c of constraints) {
    if (!REVIEW_JOINABLE.has(c.dim)) return [];
    scoped = scoped.filter(f => matchesConstraint(f, c));
  }
  return scoped.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** The tooltip sentence for a withheld quality cell, per reason. */
export function reviewWithheldReason(reason: ReviewWithheld): string {
  switch (reason) {
    case 'truncated':
      return 'Withheld: the findings window exceeded the serving cap, so any figure here would be partial. Totals return once the window fits.';
    case 'window':
      return 'Withheld: this period predates the findings window the server serves, so counts here would be partial, not zero.';
    case 'model':
    case 'author':
    case 'trigger':
    case 'job':
    case 'savingsMethod':
      return `Withheld, not zero: findings carry no ${
        reason === 'savingsMethod' ? 'savings method' : reason
      }, so they cannot be attributed under this grouping. Group by Org, Repo, Team or PR for exact review-quality figures.`;
    default:
      return 'Withheld: not attributable under this grouping.';
  }
}
